import { useSyncExternalStore } from "react";
import type { DockviewApi, DockviewGroupPanel, IDockviewPanel } from "dockview-react";
import {
  CHART_ONLY_KEY,
  chartOnlyChartCount,
  chartOnlySizes,
  parseChartOnly,
  planChartOnly,
  serializeChartOnly,
  type ChartOnlyBox,
  type ChartOnlyGridNode,
  type ChartOnlyGroup,
  type GridOrientation,
} from "@helm/core";
import type { WidgetParams } from "./templates";

/**
 * "Chart only" (owner 2026-10-05): the workspace shows its chart modules only, in the owner's own
 * arrangement; every other module is hidden. Rules: `planChartOnly` in `@helm/core`.
 *
 * How it hides (non-destructive): dockview's own group visibility (`group.api.setVisible`, the
 * mechanism its Maximize uses). A hidden docked group keeps its size aside and gives its room to
 * its neighbours; the charts are then sized to share it in the proportion they had
 * (`chartOnlySizes`), so they keep the owner's arrangement and fill the area. Nothing is closed, so
 * every module keeps its state and no chart renderer is recreated. Mixed groups bring a chart to the
 * front, and their other tabs hide by CSS (`[data-chart-only]` on the shell). Leaving shows the
 * hidden groups again in reverse order, puts every size back exactly, and the previous tabs, active
 * module and maximized group.
 *
 * The saved / synced layout is never written while it is on (`chartOnlyApplied` stops the
 * workspace's layout capture, until leaving has settled); leaving restores the same layout, so it
 * is not saved then either, unless the owner changed something meanwhile. It stays on
 * across a workspace switch (each workspace opens in Chart only) and a reload: the preference is
 * per browser (localStorage), never synced.
 */

// ---------------------------------------------------------------------------------------------
// Preference

function readPref(): boolean {
  try {
    return parseChartOnly(localStorage.getItem(CHART_ONLY_KEY));
  } catch {
    return false;
  }
}

let on = readPref();
const listeners = new Set<() => void>();

export function isChartOnly(): boolean {
  return on;
}

export function setChartOnly(next: boolean): void {
  if (next === on) return;
  on = next;
  try {
    localStorage.setItem(CHART_ONLY_KEY, serializeChartOnly(next));
  } catch {
    // Private mode / quota: it simply is not remembered after a reload.
  }
  for (const l of [...listeners]) l();
}

export function toggleChartOnly(): void {
  setChartOnly(!on);
}

export function onChartOnlyChange(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** React view of the preference (re-renders only when it is switched). */
export function useChartOnly(): boolean {
  return useSyncExternalStore(onChartOnlyChange, isChartOnly);
}

/** Opening a non-chart module (Add module, palette) leaves Chart only first, so it shows. */
export function leaveChartOnlyFor(kind: string): void {
  if (kind !== "chart") setChartOnly(false);
}

// ---------------------------------------------------------------------------------------------
// Dock

type Grid = {
  root: ChartOnlyGridNode;
  orientation: GridOrientation;
  width: number;
  height: number;
};

type Applied = {
  /** Groups this hid, in layout order (shown again in reverse). */
  hidden: DockviewGroupPanel[];
  /** Mixed groups whose front tab was switched to a chart, with the tab that was in front. */
  fronted: { group: DockviewGroupPanel; prev: IDockviewPanel }[];
  activePanel: IDockviewPanel | null;
  maximized: DockviewGroupPanel | null;
  /** The docked layout as it was (its sizes come back on leaving). */
  grid: Grid;
  /** The whole dock layout as it was (dockview's serialization), to tell whether it changed. */
  json: string;
  /** Set while leaving settles (see `leave`). */
  settling: object | null;
};

const applied = new WeakMap<DockviewApi, Applied>();
/** The workspace's layout capture per dock: run once after leaving if the layout did change. */
const captures = new WeakMap<DockviewApi, () => void>();

export function setChartOnlyCapture(api: DockviewApi, capture: () => void): void {
  captures.set(api, capture);
}

const kindOf = (p: IDockviewPanel): string | undefined => (p.params as WidgetParams | undefined)?.kind;

function describe(g: DockviewGroupPanel): ChartOnlyGroup {
  return {
    id: g.id,
    location: g.api.location.type,
    panels: g.panels.map((p) => ({ id: p.id, kind: kindOf(p) })),
    activePanelId: g.activePanel?.id ?? null,
  };
}

/** Layout (DOM) order: the dock's tree order, so groups are shown back in the reverse of hiding. */
function layoutOrder(a: DockviewGroupPanel, b: DockviewGroupPanel): number {
  if (a.element === b.element) return 0;
  return a.element.compareDocumentPosition(b.element) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
}

function gridOf(api: DockviewApi): Grid {
  const g = api.toJSON().grid;
  return {
    root: g.root,
    orientation: g.orientation,
    width: g.width,
    height: g.height,
  };
}

function afterFrames(n: number, run: () => void): void {
  if (n <= 0) run();
  else
    requestAnimationFrame(() => {
      afterFrames(n - 1, run);
    });
}

/** The docked groups and how they nest, without sizes (is it still the layout we hid from?). */
function shape(n: ChartOnlyGridNode): string {
  if (n.type === "leaf") return String((n.data as { id?: unknown }).id);
  return `(${(n.data as ChartOnlyGridNode[]).map(shape).join(",")})`;
}

/**
 * Size the docked groups exactly (dockview hands freed room to the last group of a row and takes it
 * back from the last ones; this puts each where it belongs). A few passes settle nested rows.
 */
function applySizes(api: DockviewApi, targets: Map<string, ChartOnlyBox>): void {
  for (let pass = 0; pass < 4; pass++) {
    let moved = false;
    for (const [id, b] of targets) {
      const g = api.getGroup(id);
      if (!g || g.api.location.type !== "grid") continue;
      if (g.api.width === b.width && g.api.height === b.height) continue;
      moved = true;
      g.api.setSize({ width: b.width, height: b.height });
    }
    if (!moved) return;
  }
}

/** Whether Chart only is applied to this dock (its layout must not be captured then). */
export function chartOnlyApplied(api: DockviewApi): boolean {
  return applied.has(api);
}

/** Chart modules in the dock's main window (0: the empty state shows). */
export function dockChartCount(api: DockviewApi): number {
  return chartOnlyChartCount(api.groups.map(describe));
}

function enter(api: DockviewApi): void {
  const prev = applied.get(api);
  if (prev && prev.settling === null) return;
  // Switched on again while leaving settles: the layout to come back to is still the first one.
  if (prev) applied.delete(api);
  const json = prev?.json ?? JSON.stringify(api.toJSON());
  const groups = api.groups;
  const plan = planChartOnly(groups.map(describe));
  const byId = new Map(groups.map((g) => [g.id, g]));
  const maximized = groups.find((g) => g.api.location.type === "grid" && g.api.isMaximized()) ?? null;
  if (maximized) api.exitMaximizedGroup();
  const state: Applied = {
    hidden: [],
    fronted: [],
    activePanel: api.activePanel ?? null,
    maximized,
    grid: prev?.grid ?? gridOf(api),
    json,
    settling: null,
  };
  applied.set(api, state);
  for (const f of plan.front) {
    const g = byId.get(f.groupId);
    const chart = api.getPanel(f.panelId);
    if (!g || !chart || !g.activePanel) continue;
    state.fronted.push({ group: g, prev: g.activePanel });
    chart.api.setActive();
  }
  const hide = plan.hideGroups
    .map((id) => byId.get(id))
    .filter((g): g is DockviewGroupPanel => g !== undefined)
    .sort(layoutOrder);
  for (const g of hide) {
    g.api.setVisible(false);
    state.hidden.push(g);
  }
  // The charts share the room in the proportion they had (the owner's arrangement and sizes).
  const hiddenIds = new Set(state.hidden.map((g) => g.id));
  const box = gridOf(api);
  applySizes(api, chartOnlySizes(state.grid.root, state.grid.orientation, box, hiddenIds));
  // Keyboard focus and the toolbar's "active chart" follow a chart that shows.
  const active = api.activePanel;
  if (active && state.hidden.includes(active.group)) {
    const firstChart = api.panels.find(
      (p) => kindOf(p) === "chart" && !state.hidden.includes(p.group) && p.api.location.type !== "popout",
    );
    firstChart?.api.setActive();
  }
}

function leave(api: DockviewApi): void {
  const s = applied.get(api);
  if (!s) return;
  const alive = (g: DockviewGroupPanel): boolean => api.groups.includes(g);
  for (const g of [...s.hidden].reverse()) if (alive(g)) g.api.setVisible(true);
  restoreSizes(api, s);
  for (const { group, prev } of s.fronted)
    if (alive(group) && group.panels.includes(prev)) prev.api.setActive();
  const a = s.activePanel;
  if (a && api.getPanel(a.id) === a) a.api.setActive();
  if (s.maximized && alive(s.maximized) && s.maximized.api.location.type === "grid")
    s.maximized.api.maximize();
  // Settle before the layout counts again: dockview re-lays a dock out a frame or two after a
  // visibility change (briefly at its outer size), so the capture stays off until then, the sizes
  // are put back once more, and the layout is captured only if it is not the one from before.
  const token = {};
  s.settling = token;
  afterFrames(3, () => {
    if (applied.get(api) !== s || s.settling !== token) return;
    restoreSizes(api, s);
    afterFrames(2, () => {
      if (applied.get(api) !== s || s.settling !== token) return;
      applied.delete(api);
      if (JSON.stringify(api.toJSON()) !== s.json) captures.get(api)?.();
    });
  });
}

/** The saved sizes again, unless the docked groups changed meanwhile (a chart added or closed). */
function restoreSizes(api: DockviewApi, s: Applied): void {
  const now = gridOf(api);
  if (shape(now.root) !== shape(s.grid.root) || now.orientation !== s.grid.orientation) return;
  applySizes(api, chartOnlySizes(s.grid.root, s.grid.orientation, now, new Set()));
}

/** Bring the dock in line with the preference (on ready, and whenever it is switched). */
export function syncChartOnly(api: DockviewApi): void {
  if (on) enter(api);
  else leave(api);
}
